'use client'

import { useEffect, useRef, useState } from 'react'
import Image, { getImageProps } from 'next/image'
import { cn } from '@/lib/utils'

interface ProductLoopProps {
  mp4: string
  /** Only pass when the WebM encode is actually smaller than the MP4 */
  webm?: string
  poster: string
  /** Accessible description of what the loop shows */
  label: string
  width: number
  height: number
  /** 'none' keeps below-fold loops from downloading until scrolled near */
  preload?: 'metadata' | 'none'
  /** Above-the-fold loops: preload the poster so it wins LCP */
  posterPriority?: boolean
  /** Match a CSS-hidden loop so its poster is not downloaded on small screens. */
  posterMedia?: string
  className?: string
}

/**
 * Silent looping product recording. Playback is driven by viewport
 * visibility (plays near view, pauses away), the first play waits for the
 * window load event so the video download never competes with LCP, and
 * playback starts manually so prefers-reduced-motion users keep the
 * poster frame instead of motion.
 */
export function ProductLoop({
  mp4,
  webm,
  poster,
  label,
  width,
  height,
  preload = 'metadata',
  posterPriority = false,
  posterMedia,
  className,
}: ProductLoopProps) {
  const videoRef = useRef<HTMLVideoElement>(null)
  const [playing, setPlaying] = useState<boolean>(false)
  const posterClassName = cn(
    'pointer-events-none absolute inset-0 h-auto w-full transition-opacity duration-300 motion-reduce:transition-none',
    playing ? 'opacity-0' : 'opacity-100'
  )
  const { props: posterProps } = getImageProps({
    src: poster,
    alt: '',
    width,
    height,
    sizes: '(min-width: 1024px) 896px, 100vw',
    loading: posterPriority ? 'eager' : 'lazy',
    fetchPriority: posterPriority ? 'high' : undefined,
  })

  useEffect(() => {
    const video = videoRef.current
    if (!video) return
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return
    let inView = false
    let pageLoaded = document.readyState === 'complete'
    const sync = (): void => {
      if (inView && pageLoaded) video.play().catch(() => {})
      else video.pause()
    }
    const onLoad = (): void => {
      pageLoaded = true
      sync()
    }
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          inView = entry.isIntersecting
          sync()
        }
      },
      { rootMargin: '200px 0px' }
    )
    observer.observe(video)
    if (!pageLoaded) window.addEventListener('load', onLoad, { once: true })
    return () => {
      observer.disconnect()
      window.removeEventListener('load', onLoad)
    }
  }, [])

  return (
    <div className="relative">
      <video
        ref={videoRef}
        muted
        loop
        playsInline
        preload={preload}
        aria-label={label}
        onPlaying={() => setPlaying(true)}
        className={cn('h-auto w-full', className)}
        width={width}
        height={height}
      >
        {webm ? <source src={webm} type="video/webm" /> : null}
        <source src={mp4} type="video/mp4" />
      </video>
      {/* The poster as a real image: it paints at first render, wins LCP,
          and the video frame beneath can never register a later candidate.
          Fades out only once playback actually starts. */}
      {posterMedia ? (
        <>
          {posterPriority && (
            <link
              rel="preload"
              as="image"
              media={posterMedia}
              imageSrcSet={posterProps.srcSet}
              imageSizes={posterProps.sizes}
              fetchPriority="high"
            />
          )}
          <picture>
            <source
              media={posterMedia}
              srcSet={posterProps.srcSet}
              sizes={posterProps.sizes}
            />
            {/* Next's optimized source is selected only at the visible breakpoint. */}
            <img
              {...posterProps}
              alt=""
              src="data:image/gif;base64,R0lGODlhAQABAAD/ACwAAAAAAQABAAACADs="
              srcSet={undefined}
              aria-hidden
              className={posterClassName}
            />
          </picture>
        </>
      ) : (
        <Image
          src={poster}
          alt=""
          aria-hidden
          priority={posterPriority}
          fetchPriority={posterPriority ? 'high' : undefined}
          width={width}
          height={height}
          sizes="(min-width: 1024px) 896px, 100vw"
          className={posterClassName}
        />
      )}
    </div>
  )
}
